import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import type { AppDispatch } from '/business/shared/state/store';
import getFieldErrors from '/shared/functions/getFieldErrors';
import countriesSelector from '/shared/state/selectors/countriesSelector';
import getErrorsSelector from '/shared/state/selectors/getErrorsSelector';
import hasCountriesSelector from '/shared/state/selectors/hasCountriesSelector';

import filterErrors from '/shared/functions/filterErrors';
import { clearError } from '/shared/state/actions/error';

import FieldErrors from '/shared/components/FieldErrors';
import Select from '/shared/components/widgets/select/Select';

interface Props {
  countryVal: string;
  setCountryVal: (val: string) => void;
  defaultValueI18nKey?: string;
  disabled?: boolean;
  context: string;
  defaultLanguage?: string;
  isLocationForm?: boolean;
  setDefaultLanguage?: (lang: string | undefined) => void;
}

// Define a list of countries which will always be shown.
// Normally CountrySelect only shows countries where country.enabled is true.
// But when the select is used in the electricity/heat/steam location form with
// isLocationForm, it enforces these countries will be available.
const forceAllowCountriesInLocationPage = ['us', 'ca', 'br', 'au'];

export default function CountrySelect({
  countryVal,
  isLocationForm = false,
  setCountryVal,
  defaultValueI18nKey = 'business:AddressForm.selectCountry',
  disabled,
  context,
  setDefaultLanguage = () => {},
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const errors = useSelector(getErrorsSelector);
  const _countriesUnfiltered = useSelector(countriesSelector);
  const hasCountries = useSelector(hasCountriesSelector);

  const { t } = useTranslation();
  if (!hasCountries) {
    return null;
  }
  const countries = _countriesUnfiltered.filter((country) => {
    return country.enabled || (isLocationForm && forceAllowCountriesInLocationPage.includes(country.key));
  });
  const currentCountryLine = _countriesUnfiltered.find((item) => {
    return item.key === countryVal;
  });
  if (currentCountryLine && !currentCountryLine.enabled && disabled) {
    countries.push({
      ...currentCountryLine,
    });
  }
  const countryMarketsMutated = Object.entries(countries).map(([, countryLine]) => {
    const translitName = t(`countries:countryNames.${countryLine.value}`, countryLine.value);
    return {
      ...countryLine,
      translitName,
    };
  });

  const filteredErrors = filterErrors(errors, [context]);

  const defaultPropName = t(defaultValueI18nKey);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCountry = event.target.value;
    const selectedCountryLangDefault = _countriesUnfiltered.find((item) => item.key === selectedCountry)?.langDefault;
    setCountryVal(selectedCountry);
    dispatch(clearError({ context, forElement: 'details_country_code' }));
    setDefaultLanguage(selectedCountryLangDefault);
  };

  return (
    <div>
      <Select
        testId="country_select"
        $isErrored={Boolean(getFieldErrors({ errors, forElement: 'details_country_code', context }))}
        value={countryVal}
        autoComplete="no"
        disabled={disabled}
        onDropdownChange={handleChange}
        onChange={handleChange}
      >
        <option key="none" value="">
          {defaultPropName}
        </option>
        {countryMarketsMutated.map((item) => {
          return (
            <option key={item.key} value={item.key}>
              {item.translitName}
            </option>
          );
        })}
      </Select>
      <FieldErrors errors={filteredErrors} for="details_country_code" context={context} />
    </div>
  );
}
